import React, { useState, useEffect, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'
import SettlementNoticeBanner from '../components/SettlementNoticeBanner'
import AutoFitValue from '../components/financial/AutoFitValue'
import TargetCaption from '../components/financial/TargetCaption'
import { MovementHistoryTable, VoidConfirmModal } from '../components/financial/MovementHistory'
import { LegacyBalancesSection } from '../components/financial/LegacyBalances'
import { legacySubtitle } from '../components/financial/legacy'
import {
  RESPONSIVE_CSS, money, todayISO, warnIfOverpaid, saveErrorMessage,
} from '../components/financial/formatting'

// ─────────────────────────────────────────────────────────────────────────────
//   Accounts receivable — lo que nos deben
//
//   Un lado del antiguo FinancialPage: buyers vivos y saldos heredados por
//   cobrar. Su gemela es `PayablesPage`. Todo lo que las dos usaban vive en
//   `components/financial/`; aquí sólo queda lo que es exclusivamente del lado
//   del cobro.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//   Descriptores de destino
//
//   Un cobro a un buyer vivo y un cobro contra un saldo heredado del
//   Centralizador comparten todo lo que importa: el mismo CollectionOut, el
//   mismo CollectionWriteOut con `overpaid`/`overpaid_by`, y exactamente los
//   mismos PUT/DELETE `/financial/collections/{id}`. Sólo difieren en el alta y
//   en el listado. Este objeto ES esa diferencia — y es lo que permite que la
//   lógica de sobrepago y de anulación exista una sola vez en el archivo en vez
//   de duplicarse para la sección heredada.
// ─────────────────────────────────────────────────────────────────────────────
const buyerCollectionTarget = (buyer) => ({
  title: buyer.name,
  createUrl: '/financial/collections',
  createBody: { buyer_id: buyer.id },
  listUrl: '/financial/collections',
  listParams: { buyer_id: buyer.id },
  baselineLabel: 'OWED',
  baselineValue: buyer.owed_total,
  paidLabel: 'COLLECTED',
  pendingLabel: 'OUTSTANDING',
  initialPaid: buyer.collected ?? 0,
  initialPending: buyer.outstanding ?? 0,
})

const legacyCollectionTarget = (balance) => ({
  title: balance.name,
  subtitle: legacySubtitle(balance),
  createUrl: '/financial/legacy-collections',
  createBody: { legacy_balance_id: balance.id },
  listUrl: '/financial/legacy-collections',
  listParams: { legacy_balance_id: balance.id },
  baselineLabel: 'DECLARED',
  baselineValue: balance.declared,
  paidLabel: 'SETTLED',
  pendingLabel: 'PENDING',
  initialPaid: balance.settled ?? 0,
  initialPending: balance.pending ?? 0,
})

// ─────────────────────────────────────────────────────────────────────────────
//   Modal: registrar / editar un COBRO
// ─────────────────────────────────────────────────────────────────────────────
function CollectionFormModal({ target, editing, onClose, onSaved }) {
  const isEdit = Boolean(editing)
  const [amount, setAmount] = useState(editing ? String(editing.amount ?? '') : '')
  const [date, setDate] = useState(editing?.collection_date || todayISO())
  const [reference, setReference] = useState(editing?.reference || '')
  const [note, setNote] = useState(editing?.note || '')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const amt = Number(amount)
    if (!amt || amt <= 0) { toast.error('Enter a valid amount greater than 0'); return }
    if (!date) { toast.error('Select a date'); return }
    setSaving(true)
    try {
      // El alta va al endpoint del destino (vivo o heredado); la edición
      // siempre a la ruta compartida por id — un movimiento es un movimiento y
      // la fila ya sabe qué saldo salda.
      const res = isEdit
        ? await api.put(`/financial/collections/${editing.id}`, {
            amount: amt, collection_date: date, reference: reference || null, note: note || null,
          })
        : await api.post(target.createUrl, {
            ...target.createBody, amount: amt,
            collection_date: date, reference: reference || null, note: note || null,
          })
      toast.success(isEdit ? 'Collection updated' : 'Collection registered')
      warnIfOverpaid(res.data, 'Collection')
      onSaved(res.data)
    } catch (err) {
      toast.error(saveErrorMessage(err, 'collection'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card fin-modal" style={{ maxWidth: 460, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}>{isEdit ? 'Edit collection' : 'Register collection'}</h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <TargetCaption target={target} style={{ marginTop: 0 }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          <div>
            <label className="form-label">Amount received (USD)</label>
            <input className="input fin-input" type="number" min="0" step="0.01" placeholder="0.00"
              value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Collection date</label>
            <input className="input fin-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Transaction reference <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
            <input className="input fin-input" type="text" placeholder="e.g. wire #, invoice #"
              value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Note <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
            <input className="input fin-input" type="text" placeholder="Any note"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Update' : 'Register')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Modal: histórico de COBROS (con editar / anular)
// ─────────────────────────────────────────────────────────────────────────────
function CollectionHistoryModal({ target, onClose, onChanged }) {
  const [collections, setCollections] = useState([])
  const [loading, setLoading] = useState(true)
  // Los tres importes de la cabecera vienen del backend y sólo del backend.
  // Ninguno se suma en cliente — la lista incluye filas anuladas y sumarla
  // daría un total que el backend no reconoce.
  const [totalCollected, setTotalCollected] = useState(target.initialPaid)
  const [outstanding, setOutstanding] = useState(target.initialPending)
  const [editing, setEditing] = useState(null)
  const [voiding, setVoiding] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // include_voided: las anuladas son parte de la historia y se muestran
      // intercaladas. No contaminan el saldo: `total_collected` cuenta siempre
      // sólo las activas (financial/aggregates.py, collections_for).
      const res = await api.get(target.listUrl, {
        params: { ...target.listParams, include_voided: true },
      })
      setCollections(res.data?.collections || [])
      setTotalCollected(res.data?.total_collected ?? 0)
    } catch {
      toast.error('Could not load collection history')
    } finally {
      setLoading(false)
    }
  }, [target])

  useEffect(() => { load() }, [load])

  const voidCollection = async (reason) => {
    try {
      // Ruta compartida por id, igual que la edición: sirve tanto al cobro de
      // un buyer vivo como al de un saldo heredado.
      const res = await api.delete(`/financial/collections/${voiding.id}`, { data: { reason } })
      toast.success('Collection annulled')
      setOutstanding(res.data?.pending ?? outstanding)
      setVoiding(null)
      await load()
      onChanged()
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not annul collection')
      // 409 = otra pestaña la anuló primero. Recargamos para que la fila
      // muestre su estado real en vez de volver a ofrecer los botones.
      if (err?.response?.status === 409) {
        setVoiding(null)
        await load()
        onChanged()
      }
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card fin-modal" style={{ maxWidth: 640, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>Collection history</h3>
            <TargetCaption target={target} style={{ margin: '2px 0 0' }} />
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 20, margin: '14px 0', flexWrap: 'wrap' }}>
          <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{target.baselineLabel}</div>
            <div style={{ fontWeight: 700, color: 'var(--amber)' }}>{money(target.baselineValue)}</div></div>
          <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{target.paidLabel}</div>
            <div style={{ fontWeight: 700, color: 'var(--green)' }}>{money(totalCollected)}</div></div>
          <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{target.pendingLabel}</div>
            <div style={{ fontWeight: 700, color: 'var(--red)' }}>{money(outstanding)}</div></div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : collections.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No collections registered yet.</p>
        ) : (
          <MovementHistoryTable
            rows={collections}
            dateField="collection_date"
            onEdit={setEditing}
            onVoid={setVoiding}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>

      {editing && (
        <CollectionFormModal
          target={target} editing={editing}
          onClose={() => setEditing(null)}
          onSaved={(result) => {
            setEditing(null)
            if (result) setOutstanding(result.pending)
            load()
            onChanged()
          }}
        />
      )}

      {voiding && (
        <VoidConfirmModal
          noun="collection"
          onCancel={() => setVoiding(null)}
          onConfirm={voidCollection}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Tarjeta por buyer
// ─────────────────────────────────────────────────────────────────────────────
function BuyerFinanceCard({ buyer, onRegister, onHistory }) {
  // Porcentaje de barra de progreso, no un importe: presentación pura sobre
  // dos cifras que ya vienen calculadas del backend.
  const collectedPct = buyer.owed_total > 0
    ? Math.min(100, Math.round((buyer.collected / buyer.owed_total) * 100))
    : 0

  return (
    <div className="card fade-in partners-card">
      <div className="partners-card-head">
        <div className="partners-card-title-wrap">
          <div className="partners-card-title">{buyer.name}</div>
          <div className="partners-card-subtitle">{buyer.id} · {buyer.leads} leads</div>
        </div>
      </div>

      <div className="fin-card-metrics" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)', gap: 8, marginTop: 4 }}>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--amber)">{money(buyer.owed_total)}</AutoFitValue>
          <div className="partners-metric-label">Owed</div>
        </div>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--green)">{money(buyer.collected)}</AutoFitValue>
          <div className="partners-metric-label">Collected</div>
        </div>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--red)">{money(buyer.outstanding)}</AutoFitValue>
          <div className="partners-metric-label">Outstanding</div>
        </div>
      </div>

      {buyer.owed_total > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-raised)', overflow: 'hidden' }}>
            <div style={{ width: `${collectedPct}%`, height: '100%', background: 'var(--green)' }} />
          </div>
        </div>
      )}

      <div className="fin-card-actions" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
          onClick={() => onRegister(buyer)}
          disabled={buyer.owed_total <= 0}
          title={buyer.owed_total <= 0 ? 'Nothing owed yet' : 'Register a collection'}>
          Register collection
        </button>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => onHistory(buyer)}>
          Collection history
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Página
// ─────────────────────────────────────────────────────────────────────────────
export default function ReceivablesPage() {
  const [buyersData, setBuyersData] = useState({ buyers: [], totals: {} })
  const [legacy, setLegacy] = useState({ receivables: [], totals: {}, error: false })
  const [loading, setLoading] = useState(true)
  const [collectFor, setCollectFor] = useState(null)
  const [collectHistoryFor, setCollectHistoryFor] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [bRes, lRes] = await Promise.all([
        api.get('/financial/buyers-summary'),
        // Los saldos heredados son un añadido; los buyers vivos son lo
        // principal. Si el endpoint falla, la página no puede quedarse en
        // blanco — se marca el fallo y la sección lo dice en su sitio.
        //
        // `/financial/legacy-summary` no acepta filtro por lado: devuelve
        // payables y receivables juntos y esta página se queda con su mitad.
        // /payables hace lo propio con la otra.
        api.get('/financial/legacy-summary').catch(() => null),
      ])
      setBuyersData({ buyers: bRes.data?.buyers || [], totals: bRes.data?.totals || {} })
      // `as_of` y `as_of_mixed` del envelope se descartan a propósito: los
      // calcula el backend sobre los dos lados juntos y aquí sólo se enseña
      // uno. LegacyBalancesSection los deriva de estas filas — ver cutOffOf()
      // en components/financial/legacy.js.
      setLegacy({
        receivables: lRes?.data?.receivables || [],
        totals: lRes?.data?.totals || {},
        error: lRes === null,
      })
    } catch {
      toast.error('Could not load receivables')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const t = buyersData.totals || {}
  const lt = legacy.totals || {}

  // Algunas contrapartes heredadas existen también en la sección viva de
  // arriba, con saldos distintos y sin relación. Marcarlas es lo que evita que
  // alguien vea el mismo nombre dos veces con cifras diferentes y lo lea como
  // un descuadre. El cruce es por nombre porque es lo único que comparten: un
  // saldo heredado no referencia al buyer actual — sus leads nunca se
  // migraron. Si el backend renombra a una, el badge simplemente deja de
  // salir; nunca aparece uno falso.
  //
  // Deliberadamente sólo contra los buyers vivos. Antes de partir la página el
  // cruce era indiscriminado (buyers Y partners contra los dos lados
  // heredados), así que un receivable heredado se marcaba "Also above" porque
  // su nombre coincidía con el de un partner — una contraparte que no aparece
  // en ninguna parte de esta página. Ahora cada lado se cruza sólo con el suyo.
  const liveNames = useMemo(
    () => new Set(
      buyersData.buyers
        .map((x) => x.name)
        .filter(Boolean)
        .map((n) => String(n).trim().toLowerCase()),
    ),
    [buyersData.buyers],
  )

  return (
    <div className="fade-in">
      <style>{RESPONSIVE_CSS}</style>

      {/* No range on purpose. `/financial/buyers-summary` and
          `/financial/legacy-summary` take no date parameter - what this page
          shows are lifetime cumulative balances, not a period - and the
          backend reads an absent bound as an open end, so this asks for every
          notice that bears on them. Narrowing it to, say, the current year
          would hide exactly the carried-over agreement that explains an
          outstanding balance. /payables mounts the same banner over its own
          endpoints; the scoping is server-side (companies/settlements.py,
          notices_for()), so each page gets what its caller may see rather
          than a copy filtered here. */}
      <div style={{ marginBottom: 20 }}>
        <SettlementNoticeBanner />
      </div>

      <div className="page-header">
        <div className="hero-kicker">Financial</div>
        <h1>Accounts receivable</h1>
        <p style={{ color: 'var(--text-muted)', maxWidth: 720 }}>
          What buyers owe us for won leads, and the collections received against it.
        </p>
      </div>

      <div className="fin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Total owed to us', value: money(t.owed_total), color: 'var(--amber)' },
          { label: 'Collected', value: money(t.collected), color: 'var(--green)' },
          { label: 'Outstanding', value: money(t.outstanding), color: 'var(--red)' },
        ].map((s) => (
          <div key={s.label} className="card" style={{ padding: '18px 12px', textAlign: 'center', minWidth: 0, overflow: 'hidden' }}>
            <AutoFitValue color={s.color} max={24} min={13}>{s.value}</AutoFitValue>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : (
        <div className="fin-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
          {buyersData.buyers.map((b) => (
            <BuyerFinanceCard
              key={b.id}
              buyer={b}
              onRegister={(x) => setCollectFor(buyerCollectionTarget(x))}
              onHistory={(x) => setCollectHistoryFor(buyerCollectionTarget(x))}
            />
          ))}
        </div>
      )}

      {!loading && (
        <LegacyBalancesSection
          balances={legacy.receivables}
          error={legacy.error}
          liveNames={liveNames}
          title="Receivables · they owe us"
          caption="Left uncollected from buyers when the previous system was switched off."
          stats={[
            { label: 'Declared', value: lt.receivable_declared, color: 'var(--amber)' },
            { label: 'Settled', value: lt.receivable_settled, color: 'var(--green)' },
            { label: 'Outstanding', value: lt.receivable_outstanding, color: 'var(--red)' },
          ]}
          actionLabel="Register collection"
          historyLabel="Collection history"
          onRegister={(b) => setCollectFor(legacyCollectionTarget(b))}
          onHistory={(b) => setCollectHistoryFor(legacyCollectionTarget(b))}
        />
      )}

      {collectFor && (
        <CollectionFormModal
          target={collectFor}
          editing={null}
          onClose={() => setCollectFor(null)}
          onSaved={() => { setCollectFor(null); load() }}
        />
      )}

      {collectHistoryFor && (
        <CollectionHistoryModal
          target={collectHistoryFor}
          onClose={() => setCollectHistoryFor(null)}
          onChanged={load}
        />
      )}
    </div>
  )
}
